import type { AdsgramController } from '@/types/adsgram'

export class RewardedAdManager {
  private rewardedAdController: AdsgramController | null = null
  private initPromise: Promise<AdsgramController | null> | null = null
  private readonly blockId = '17672' // Rewarded ad block ID from Adsgram dashboard
  
  constructor() {}

  private async initializeController(): Promise<AdsgramController | null> {
    if (this.initPromise) {
      return this.initPromise
    }

    this.initPromise = new Promise<AdsgramController | null>((resolve) => {
      const initialize = (): AdsgramController | null => {
        if (!window.Adsgram?.init) {
          return null
        }

        try {
          return window.Adsgram.init({ blockId: this.blockId })
        } catch (error) {
          console.error('Adsgram rewarded init failed', error)
          return null
        }
      }

      const controller = initialize()
      if (controller) {
        resolve(controller)
        return
      }

      let attempts = 0
      const maxAttempts = 20
      const interval = window.setInterval(() => {
        attempts += 1
        const pendingController = initialize()
        if (pendingController) {
          window.clearInterval(interval)
          resolve(pendingController)
        } else if (attempts >= maxAttempts) {
          window.clearInterval(interval)
          resolve(null)
        }
      }, 200)
    })

    this.rewardedAdController = await this.initPromise
    return this.rewardedAdController
  }

  async showRewardedAd(): Promise<{ success: boolean; error?: string }> {
    try {
      const controller = await this.initializeController()
      if (!controller) {
        return { success: false, error: 'Rewarded ads not available' }
      }

      const result = await controller.show()
      
      if (result.done && !result.error) {
        return { success: true }
      } else {
        return { 
          success: false, 
          error: result.description || 'Ad was not completed' 
        }
      }
    } catch (error) {
      console.error('Rewarded ad show failed', error)
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      }
    }
  }

  isAvailable(): boolean {
    return !!window.Adsgram?.init
  }
}